import { PassengerTypeEnum } from 'api-hooks/booking/model';
import { useGetBooking } from 'api-hooks/booking/query';
import { EmptyFacility } from 'assets/images/svg';
import Stack from 'components/commons/stack';
import Text from 'components/commons/text';
import FetchWrapperComponent from 'components/elements/fetch-wrapper-component';
import { TabItem, Tabs } from 'components/widgets/tabs';
import { usePanel } from 'hooks/use-panel';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import FacilityDetail from './passenger-facility-detail.tsx/facility-detail';
import PassengerDetail from './passenger-facility-detail.tsx/passenger-detail';
import PriceDetailTab from './price-detail';
import { PanelContent } from './style';

interface Props {
  bookingId?: string;
  trigger: (toggle: any) => React.ReactNode;
}

interface DetailProps {
  bookingId: string;
}

interface passengerItem {
  qty: number;
  basePrice: number;
  insuranceQty: number;
  insurancePrice: number;
  accomodationQty: number;
  accomodationInsuranceQty: number;
}

interface dataPassenger extends passengerItem {
  name: string;
}
interface vehiclesItem {
  name: string;
  qty: number;
  basePrice: number;
  insuranceQty?: number;
  insurancePrice?: number;
  isFree?: boolean;
}
interface facilitiesItem {
  name: string;
  variant: string;
  qty: number;
  price: number;
}

function DetailComponent(props: DetailProps) {
  const { t } = useTranslation();
  const { data, error, isLoading, isFetching, refetch } = useGetBooking({
    bookingId: props?.bookingId!,
  });
  const passengerData = React.useMemo<dataPassenger[]>(() => {
    if (data?.data) {
      const _item: { adult: passengerItem; infant: passengerItem } =
        data.data.passengers.reduce(
          (prev, curr) => {
            const isAdult = curr.type === PassengerTypeEnum.Adult;
            const isInfant = curr.type === PassengerTypeEnum.Infant;
            return {
              adult: {
                qty: isAdult ? prev.adult.qty + 1 : prev.adult.qty,
                basePrice: isAdult ? curr.price : prev.adult.basePrice,
                insuranceQty:
                  isAdult && curr.isInsurance
                    ? prev.adult.insuranceQty + 1
                    : prev.adult.insuranceQty,
                insurancePrice:
                  isAdult && curr.isInsurance
                    ? curr.insurancePrice
                    : prev.adult.insurancePrice,
                accomodationQty:
                  isAdult && curr.isFacilitated
                    ? prev.adult.accomodationQty + 1
                    : prev.adult.accomodationQty,
                accomodationInsuranceQty:
                  isAdult && curr.isFacilitated && curr.isInsurance
                    ? prev.adult.accomodationInsuranceQty + 1
                    : prev.adult.accomodationInsuranceQty,
              },
              infant: {
                qty: isInfant ? prev.infant.qty + 1 : prev.infant.qty,
                basePrice: isInfant ? curr.price : prev.infant.basePrice,
                insuranceQty:
                  isInfant && curr.isInsurance
                    ? prev.infant.insuranceQty + 1
                    : prev.infant.insuranceQty,
                insurancePrice:
                  isInfant && curr.isInsurance
                    ? curr.insurancePrice
                    : prev.infant.insurancePrice,
                accomodationQty:
                  isInfant && curr.isFacilitated
                    ? prev.infant.accomodationQty + 1
                    : prev.infant.accomodationQty,
                accomodationInsuranceQty:
                  isInfant && curr.isFacilitated && curr.isInsurance
                    ? prev.infant.accomodationInsuranceQty + 1
                    : prev.infant.accomodationInsuranceQty,
              },
            };
          },
          {
            adult: {
              qty: 0,
              basePrice: 0,
              insuranceQty: 0,
              insurancePrice: 0,
              accomodationQty: 0,
              accomodationInsuranceQty: 0,
            },
            infant: {
              qty: 0,
              basePrice: 0,
              insuranceQty: 0,
              insurancePrice: 0,
              accomodationQty: 0,
              accomodationInsuranceQty: 0,
            },
          },
        );
      return Object.keys(_item)
        .map<dataPassenger>((key) => {
          return {
            ..._item[key],
            name: `${t(`common:${key}`)}`,
          };
        })
        .filter((value) => value.qty);
    }
    return [];
  }, [data, t]);

  const vehicleData = React.useMemo(() => {
    const _vehicleData: vehiclesItem[] = [];
    if (data?.data) {
      const _itemVehicle = data.data.vehicles.reduce(
        (prev, curr) => {
          return {
            name: curr.vehicleType.name,
            qty: 1,
            basePrice: curr.price,
            insuranceQty: curr.isInsurance ? 1 : 0,
            insurancePrice: curr.insurancePrice,
          };
        },
        { name: '', qty: 0, basePrice: 0, insuranceQty: 0, insurancePrice: 0 },
      );
      if (_itemVehicle.qty) {
        _vehicleData.push(_itemVehicle);
      }
      passengerData
        .filter((item) => item.accomodationQty)
        .forEach((item) => {
          _vehicleData.push({
            name: `${t('common:free')} ${t('common:passenger')} ${item.name}`,
            basePrice: item.basePrice,
            qty: item.accomodationQty,
            isFree: true,
          });
        });
      passengerData
        .filter((item) => item.accomodationQty && item.insuranceQty)
        .forEach((item) => {
          _vehicleData.push({
            name: `${t('common:free')} ${t('common:insurance')} ${item.name}`,
            basePrice: item.insurancePrice,
            qty: item.accomodationInsuranceQty,
            isFree: true,
          });
        });
      return _vehicleData;
    }
    return [];
  }, [data, passengerData, t]);

  const facilitiesData = React.useMemo<
    { name: string; basePrice: number; qty: number }[]
  >(() => {
    if (data?.data) {
      const _facilityData: { [key: string]: facilitiesItem } =
        data.data.facilities.reduce((prevValue, currItem) => {
          if (prevValue[currItem.variant]) {
            prevValue[currItem.variant].qty =
              prevValue[currItem.variant].qty + 1;
          } else {
            const value: facilitiesItem = {
              name: currItem.name,
              variant: currItem.variant,
              price: currItem.price,
              qty: 1,
            };
            prevValue[currItem.variant] = value;
          }
          return prevValue;
        }, {});
      return Object.keys(_facilityData).map((value) => {
        return {
          name: t(`booking:${_facilityData[value].name}_variant`, {
            variant: _facilityData[value].variant,
          }),
          basePrice: _facilityData[value].price,
          qty: _facilityData[value].qty,
        };
      });
    }
    return [];
  }, [data, t]);
  return (
    <FetchWrapperComponent
      error={error}
      onRetry={refetch}
      isLoading={isLoading || isFetching}
      component={
        <PanelContent>
          <Tabs
            defaultActiveKey="common:passenger-detail"
            aria-label="order-details"
            tabListItemCss={{
              flex: 1,
              px: 0,
              '&:nth-child(2)': {
                display:
                  !!passengerData.length || facilitiesData.length
                    ? 'flex'
                    : 'none',
              },
            }}
            tabListCss={{ overflowX: 'inherit', mx: 24, mt: 32 }}
            tabMinWidth={0}
            tabPanelCss={{
              padding: 0,
              flex: 1,
              height: '100%',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
            css={{ flex: 1 }}
          >
            <TabItem
              title={
                passengerData.length
                  ? t('common:passenger_detail')
                  : t('booking:vehicle_detail')
              }
              key="common:passenger-detail"
            >
              {!!data?.data?.passengers?.length ||
              !!data?.data?.vehicles?.length ? (
                <PassengerDetail
                  data={
                    (
                      data?.data?.passengers?.map((cur, idx) => {
                        return {
                          type: 'passenger',
                          headerText: `${t('booking:passenger_extra', {
                            extra: idx + 1,
                          })} (${t(`common:${cur.type}`)})`,
                          passengerName: cur.name,
                          identityId: cur.identifierCode,
                          seatNumber: cur.seatNumber,
                          roomNumber: cur?.facility?.code || '-',
                        };
                      }) || []
                    ).concat(
                      (data?.data?.vehicles?.map((vehicle) => {
                        const vehicleOnly =
                          !data?.data?.passengers?.length &&
                          !data?.data?.facilities?.length;

                        const headerText = (() => {
                          const labels: string[] = [];
                          labels.push(
                            `${t('booking:vehicle_extra', {
                              extra: vehicle?.vehicleType?.name,
                            })}`,
                          );
                          if (
                            typeof vehicle.vehicleType.maxHeight === 'string'
                          ) {
                            labels.push(
                              t(`booking:max_height_extra`, {
                                extra: vehicle.vehicleType.maxHeight,
                              }),
                            );
                          }

                          return labels.join(' ');
                        })();
                        return {
                          type: vehicleOnly ? 'vehicle-only' : 'vehicle',
                          ...(vehicleOnly
                            ? {
                                expedition: vehicle?.expedition,
                                condition: vehicle?.condition,
                                content: vehicle?.vehicleContentType?.name,
                                vehicleDetail: vehicle?.description,
                                document: vehicle?.documents?.map(
                                  (item) => item.url,
                                ),
                              }
                            : {
                                driverName: vehicle?.driver?.name,
                                passenger:
                                  vehicle?.passengers
                                    ?.map((passenger) => passenger.name)
                                    .join(', ') || '',
                              }),
                          platNumber: vehicle?.identifierCode,
                          headerText,
                        };
                      }) as any[]) || [],
                    ) as any[]
                  }
                />
              ) : (
                <Stack rootCss={{ padding: 16 }}>
                  <Text align="center">
                    {t('booking:passenger_vehicle_empty')}
                  </Text>
                </Stack>
              )}
            </TabItem>
            <TabItem
              title={t('common:facility_detail')}
              key="common:facility-detail"
            >
              {data?.data?.facilities?.length ? (
                <FacilityDetail
                  data={data?.data?.facilities?.map((facility) => ({
                    passengerName: facility?.passengers
                      ?.map((passenger) => passenger.name)
                      .join(', '),
                    number: facility?.code,
                    totalPrice: facility?.price?.toString(),
                    headerText: t(`booking:${facility?.name}`),
                  }))}
                />
              ) : (
                <Stack rootCss={{ px: 16, pt: 32, pb: 16 }} alignItems="center">
                  <EmptyFacility />
                  <Text
                    align="center"
                    css={{ mt: 16 }}
                    variant="headline5"
                    weight="bold"
                  >
                    {t('booking:facility_empty')}
                  </Text>
                  <Text align="center" css={{ mt: 16 }} variant="body4">
                    {t('booking:you_did_not_purchase_the_facility')}
                  </Text>
                </Stack>
              )}
            </TabItem>
            <TabItem title={t('common:price_detail')} key="common:price-detail">
              <PriceDetailTab
                data={{
                  totalPrice: data?.data?.totalPrice?.toString() || '-',
                  details: [
                    {
                      type: 'passenger',
                      items: passengerData,
                      // items: priceObj.passengers,
                    },
                    {
                      type: 'vehicle',
                      items: vehicleData,
                    },
                    {
                      type: 'facility',
                      items: facilitiesData,
                    },
                  ],
                }}
              />
            </TabItem>
          </Tabs>
        </PanelContent>
      }
    />
  );
}
export default function DetailPanel(props: Props) {
  const { t } = useTranslation();
  const panel = usePanel();

  const onDetail = React.useCallback(
    (bookingId) =>
      panel.onAddPanel({
        title: t('common:detail'),
        width: 900,
        children: (onClose) => <DetailComponent bookingId={bookingId} />,
      }),
    [panel, t],
  );
  return <>{props.trigger(() => onDetail(props.bookingId))}</>;
}
